<template>
  <div>
    <app-spinner v-if="!page_loaded" />
    <template v-else>
      <app-cells>
        <app-button @click.native="onShowModal">New request</app-button>
        <app-input
          @input.native="onItemSearch"
          v-model="search"
          class="input--300"
          type="search"
          placeholder="Search"
          name="search"
        />
        <app-select
          @input="onChangeFilter"
          v-model="filter_legal_entity"
          :options="legal_entity_options"
          :reduce="item => item.id"
          placeholder="Legal entity"
          class="select--300"
        />
      </app-cells>
      <template v-if="table.items.length">
        <app-cells><h2 class="title">Draft</h2></app-cells>
        <v-client-table
          :data="table.items"
          :columns="table.columns"
          :options="table.options"
          class="table-default"
          :class="{ 'table-default--loading': table_preloader }"
        >
          <template #h__id><div class="table-default__left">ID</div></template>
          <template #h__responsible><div class="table-default__left">Responsible</div></template>
          <template #id="{ row }">
            <div class="table-default__left">
              <router-link :to="{name: 'finance-fund-requests-detail-items', params: {id: row.id }}" class="table-default__link-border">
                Request - {{ row.id }}
              </router-link>
            </div>
          </template>
          <template #date="{ row }">{{ dateFormat(row.date) }}</template>
          <template #responsible="{ row }"><div class="table-default__left">{{ row.responsible.full_name }}</div></template>
          <template #total_estimated_amount="{ row }">
            {{ new Intl.NumberFormat('ru-RU', {minimumFractionDigits: 2}).format(row.total_estimated_amount) }} {{ row.currency.code }}
          </template>
        </v-client-table>
        <app-cells position="between">
          <div>
            <paginate
              :clickHandler="onClickPagination"
              v-if="pagination.pages > 1"
              v-model="pagination.page"
              :page-count="pagination.pages"
              :prev-text="'<'"
              :next-text="'>'"
              :container-class="'pagination'"
              :page-class="'pagination__item'"
              :page-link-class="'pagination__link'"
              :prev-class="'pagination__item pagination__item--prev'"
              :next-class="'pagination__item pagination__item--next'"
              :next-link-class="'pagination__link pagination__link--next'"
              :prev-link-class="'pagination__link pagination__link--prev'"
            />
          </div>
          <app-table-records :all="pagination.count" :count="table.items.length" :page="pagination.page" />
        </app-cells>
      </template>
      <template v-if="table_requested.items.length">
        <app-cells><h2 class="title">Requested</h2></app-cells>
        <v-client-table
          :data="table_requested.items"
          :columns="table_requested.columns"
          :options="table_requested.options"
          class="table-default"
          :class="{ 'table-default--loading': table_preloader_requested }"
        >
          <template #h__id><div class="table-default__left">ID</div></template>
          <template #h__responsible><div class="table-default__left">Responsible</div></template>
          <template #h__approval><div class="table-default__left">Waiting approval</div></template>
          <template #id="{ row }">
            <div class="table-default__left">
              <router-link :to="{name: 'finance-fund-requests-detail-items', params: {id: row.id }}" class="table-default__link-border">
                Request - {{ row.id }}
              </router-link>
            </div>
          </template>
          <template #date="{ row }">{{ dateFormat(row.date) }}</template>
          <template #responsible="{ row }"><div class="table-default__left">{{ row.responsible.full_name }}</div></template>
          <template #approval="{ row }"><div class="table-default__left">{{ row.responsible_for_approval?.full_name || '-' }}</div></template>
          <template #total_estimated_amount="{ row }">
            {{ new Intl.NumberFormat('ru-RU', {minimumFractionDigits: 2}).format(row.total_estimated_amount) }} {{ row.currency.code }}
          </template>
        </v-client-table>
        <app-cells position="between">
          <div>
            <paginate
              :clickHandler="onClickPaginationRequested"
              v-if="pagination_requested.pages > 1"
              v-model="pagination_requested.page"
              :page-count="pagination_requested.pages"
              :prev-text="'<'"
              :next-text="'>'"
              :container-class="'pagination'"
              :page-class="'pagination__item'"
              :page-link-class="'pagination__link'"
              :prev-class="'pagination__item pagination__item--prev'"
              :next-class="'pagination__item pagination__item--next'"
              :next-link-class="'pagination__link pagination__link--next'"
              :prev-link-class="'pagination__link pagination__link--prev'"
            />
          </div>
          <app-table-records :all="pagination_requested.count" :count="table_requested.items.length" :page="pagination_requested.page" />
        </app-cells>
      </template>
      <template v-if="table_approved.items.length">
        <app-cells><h2 class="title">Waiting payment</h2></app-cells>
        <v-client-table
          :data="table_approved.items"
          :columns="table_approved.columns"
          :options="table_approved.options"
          class="table-default"
          :class="{ 'table-default--loading': table_preloader_approved }"
        >
          <template #h__id><div class="table-default__left">ID</div></template>
          <template #h__responsible><div class="table-default__left">Responsible</div></template>
          <template #id="{ row }">
            <div class="table-default__left">
              <router-link :to="{name: 'finance-fund-requests-detail-items', params: {id: row.id }}" class="table-default__link-border">
                Request - {{ row.id }}
              </router-link>
            </div>
          </template>
          <template #date="{ row }">{{ dateFormat(row.date) }}</template>
          <template #responsible="{ row }"><div class="table-default__left">{{ row.responsible.full_name }}</div></template>
          <template #total_estimated_amount="{ row }">
            {{ new Intl.NumberFormat('ru-RU', {minimumFractionDigits: 2}).format(row.total_estimated_amount) }} {{ row.currency.code }}
          </template>
          <template #paid="{ row }">
            <span
              class="status-tag"
              :style="{'backgroundColor': row.paid ? '#77b017' : '#e85c20'}"
            >
              {{ new Intl.NumberFormat('ru-RU', {minimumFractionDigits: 2}).format(row.amount_paid) }} {{ row.currency.code }}
            </span>
          </template>
          <template #efd="{ row }">
            <span
              class="status-tag"
              :style="{'backgroundColor': row.efd ? '#77b017' : '#e85c20'}"
            >
              {{ new Intl.NumberFormat('ru-RU', {minimumFractionDigits: 2}).format(row.amount_efd) }} {{ row.currency.code }}
            </span>
          </template>
        </v-client-table>
        <app-cells position="between">
          <div>
            <paginate
              :clickHandler="onClickPaginationApproved"
              v-if="pagination_approved.pages > 1"
              v-model="pagination_approved.page"
              :page-count="pagination_approved.pages"
              :prev-text="'<'"
              :next-text="'>'"
              :container-class="'pagination'"
              :page-class="'pagination__item'"
              :page-link-class="'pagination__link'"
              :prev-class="'pagination__item pagination__item--prev'"
              :next-class="'pagination__item pagination__item--next'"
              :next-link-class="'pagination__link pagination__link--next'"
              :prev-link-class="'pagination__link pagination__link--prev'"
            />
          </div>
          <app-table-records :all="pagination_approved.count" :count="table_approved.items.length" :page="pagination_approved.page" />
        </app-cells>
      </template>
      <template v-if="table_paid.items.length">
        <app-cells><h2 class="title">Pending EFD</h2></app-cells>
        <v-client-table
          :data="table_paid.items"
          :columns="table_paid.columns"
          :options="table_paid.options"
          class="table-default"
          :class="{ 'table-default--loading': table_preloader_paid }"
        >
          <template #h__id><div class="table-default__left">ID</div></template>
          <template #h__responsible><div class="table-default__left">Responsible</div></template>
          <template #id="{ row }">
            <div class="table-default__left">
              <router-link :to="{name: 'finance-fund-requests-detail-items', params: {id: row.id }}" class="table-default__link-border">
                Request - {{ row.id }}
              </router-link>
            </div>
          </template>
          <template #date="{ row }">{{ dateFormat(row.date) }}</template>
          <template #responsible="{ row }"><div class="table-default__left">{{ row.responsible.full_name }}</div></template>
          <template #amount_total="{ row }">
            {{ new Intl.NumberFormat('ru-RU', {minimumFractionDigits: 2}).format(row.amount_total) }} {{ row.currency.code }}
          </template>
          <template #paid="{ row }">
            <span
              class="status-tag"
              :style="{'backgroundColor': row.paid ? '#77b017' : '#e85c20'}"
            >
              {{ new Intl.NumberFormat('ru-RU', {minimumFractionDigits: 2}).format(row.amount_paid) }} {{ row.currency.code }}
            </span>
          </template>
          <template #efd="{ row }">
            <span
              class="status-tag"
              :style="{'backgroundColor': row.efd ? '#77b017' : '#e85c20'}"
            >
              {{ new Intl.NumberFormat('ru-RU', {minimumFractionDigits: 2}).format(row.amount_efd) }} {{ row.currency.code }}
            </span>
          </template>
        </v-client-table>
        <app-cells position="between">
          <div>
            <paginate
              :clickHandler="onClickPaginationPaid"
              v-if="pagination_paid.pages > 1"
              v-model="pagination_paid.page"
              :page-count="pagination_paid.pages"
              :prev-text="'<'"
              :next-text="'>'"
              :container-class="'pagination'"
              :page-class="'pagination__item'"
              :page-link-class="'pagination__link'"
              :prev-class="'pagination__item pagination__item--prev'"
              :next-class="'pagination__item pagination__item--next'"
              :next-link-class="'pagination__link pagination__link--next'"
              :prev-link-class="'pagination__link pagination__link--prev'"
            />
          </div>
          <app-table-records :all="pagination_paid.count" :count="table_paid.items.length" :page="pagination_paid.page" />
        </app-cells>
      </template>
      <template v-if="table_review.items.length">
        <app-cells><h2 class="title">Review and complete</h2></app-cells>
        <v-client-table
          :data="table_review.items"
          :columns="table_review.columns"
          :options="table_review.options"
          class="table-default"
          :class="{ 'table-default--loading': table_preloader_review }"
        >
          <template #h__id><div class="table-default__left">ID</div></template>
          <template #h__responsible><div class="table-default__left">Responsible</div></template>
          <template #id="{ row }">
            <div class="table-default__left">
              <router-link :to="{name: 'finance-fund-requests-detail-items', params: {id: row.id }}" class="table-default__link-border">
                Request - {{ row.id }}
              </router-link>
            </div>
          </template>
          <template #date="{ row }">{{ dateFormat(row.date) }}</template>
          <template #responsible="{ row }"><div class="table-default__left">{{ row.responsible.full_name }}</div></template>
          <template #amount_total="{ row }">
            {{ new Intl.NumberFormat('ru-RU', {minimumFractionDigits: 2}).format(row.amount_total) }} {{ row.currency.code }}
          </template>
          <template #paid="{ row }">
            <span
              class="status-tag"
              :style="{'backgroundColor': row.paid ? '#77b017' : '#e85c20'}"
            >
              {{ new Intl.NumberFormat('ru-RU', {minimumFractionDigits: 2}).format(row.amount_paid) }} {{ row.currency.code }}
            </span>
          </template>
          <template #efd="{ row }">
            <span
              class="status-tag"
              :style="{'backgroundColor': row.efd ? '#77b017' : '#e85c20'}"
            >
              {{ new Intl.NumberFormat('ru-RU', {minimumFractionDigits: 2}).format(row.amount_efd) }} {{ row.currency.code }}
            </span>
          </template>
        </v-client-table>
        <app-cells position="between">
          <div>
            <paginate
              :clickHandler="onClickPaginationReview"
              v-if="pagination_review.pages > 1"
              v-model="pagination_review.page"
              :page-count="pagination_review.pages"
              :prev-text="'<'"
              :next-text="'>'"
              :container-class="'pagination'"
              :page-class="'pagination__item'"
              :page-link-class="'pagination__link'"
              :prev-class="'pagination__item pagination__item--prev'"
              :next-class="'pagination__item pagination__item--next'"
              :next-link-class="'pagination__link pagination__link--next'"
              :prev-link-class="'pagination__link pagination__link--prev'"
            />
          </div>
          <app-table-records :all="pagination_review.count" :count="table_review.items.length" :page="pagination_review.page" />
        </app-cells>
      </template>
    </template>
    <modal
      name="request-form"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="onCloseModal" class="popup__close" type="button"></button>
        <div class="popup__title">New request</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckForm">
            <app-form-group label="Date" required>
              <app-date-picker
                v-model="form.date"
                placeholder="Date"
                :error="$v.form.date.$error"
              />
              <template #error>
                <div v-if="$v.form.date.$dirty && !$v.form.date.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Legal entity" required>
              <app-select
                @input="onChangeLegalEntity"
                v-model="form.legal_entity"
                :options="legal_entity_options"
                :reduce="item => item.code"
                :clearable="false"
                placeholder="Legal entity"
                :error="$v.form.legal_entity.$error"
              />
              <template #error>
                <div v-if="$v.form.legal_entity.$dirty && !$v.form.legal_entity.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Business unit" required>
              <app-select
                v-model="form.business_unit"
                :options="business_unit_options"
                :reduce="item => item.id"
                :clearable="false"
                :readonly="!form.legal_entity"
                placeholder="Business unit"
                :error="$v.form.business_unit.$error"
              />
              <template #error>
                <div v-if="$v.form.business_unit.$dirty && !$v.form.business_unit.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Responsible" required>
              <app-select
                @search="onEmployeeSearch"
                v-model="form.responsible"
                :options="responsible_options"
                :clearable="false"
                label="full_name"
                placeholder="Responsible"
                :error="$v.form.responsible.$error"
              />
              <template #error>
                <div v-if="$v.form.responsible.$dirty && !$v.form.responsible.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Responsible for approval" required>
              <app-select
                v-model="form.responsible_for_approval"
                :options="responsible_approval_options"
                :reduce="item => item.id"
                :clearable="false"
                label="full_name"
                placeholder="Responsible for approval"
                :error="$v.form.responsible_for_approval.$error"
              />
              <template #error>
                <div v-if="$v.form.responsible_for_approval.$dirty && !$v.form.responsible_for_approval.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Currency" required>
              <app-select
                v-model="form.currency"
                :options="currency_options"
                :reduce="item => item.code"
                :clearable="false"
                placeholder="Currency"
                :error="$v.form.currency.$error"
              />
              <template #error>
                <div v-if="$v.form.currency.$dirty && !$v.form.currency.required">Required field</div>
              </template>
            </app-form-group>
            <app-cells position="center" :indent="false">
              <app-button :disabled="$v.form.$error" type="submit" ref="submit">Create request</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import {
  getAdministrativeBusinessUnits,
  getAdministrativeLegalEntities,
  getCurrencyList,
  getEmployeesListSearch,
  getFinanceFundRequests,
  postFinanceFundRequests,
} from '@/http'
import debounce from 'lodash/debounce'
import { format, parseISO } from 'date-fns'
import { required } from 'vuelidate/lib/validators'

export default {
  name: 'FinanceFundRequestsActive',
  data() {
    return {
      page_loaded: false,
      search: '',
      filter_legal_entity: null,
      legal_entity_options: [],
      table: {
        items: [],
        columns: ['id', 'date', 'legal_entity.name', 'responsible', 'business_unit.name', 'total_estimated_amount'],
        options: {
          headings: {
            'id': 'ID',
            'date': 'Date',
            'legal_entity.name': 'Legal entity',
            'responsible': 'Responsible',
            'business_unit.name': 'Business unit',
            'total_estimated_amount': 'Est. amount',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 20,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination: {
        pages: 1,
        page: 1,
        count: 1,
      },
      table_preloader: false,
      table_requested: {
        items: [],
        columns: ['id', 'date', 'legal_entity.name', 'responsible', 'approval', 'business_unit.name', 'total_estimated_amount'],
        options: {
          headings: {
            'id': 'ID',
            'date': 'Date',
            'legal_entity.name': 'Legal entity',
            'responsible': 'Responsible',
            'approval': 'Waiting approval',
            'business_unit.name': 'Business unit',
            'total_estimated_amount': 'Est. amount',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 20,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination_requested: {
        pages: 1,
        page: 1,
        count: 1,
      },
      table_preloader_requested: false,
      table_approved: {
        items: [],
        columns: ['id', 'date', 'legal_entity.name', 'responsible', 'business_unit.name', 'total_estimated_amount', 'paid', 'efd'],
        options: {
          headings: {
            'id': 'ID',
            'date': 'Date',
            'legal_entity.name': 'Legal entity',
            'responsible': 'Responsible',
            'business_unit.name': 'Business unit',
            'total_estimated_amount': 'Est. amount',
            'paid': 'Paid',
            'efd': 'EFD',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 20,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination_approved: {
        pages: 1,
        page: 1,
        count: 1,
      },
      table_preloader_approved: false,
      table_paid: {
        items: [],
        columns: ['id', 'date', 'legal_entity.name', 'responsible', 'business_unit.name', 'amount_total', 'paid', 'efd'],
        options: {
          headings: {
            'id': 'ID',
            'date': 'Date',
            'legal_entity.name': 'Legal entity',
            'responsible': 'Responsible',
            'business_unit.name': 'Business unit',
            'amount_total': 'Total',
            'paid': 'Paid',
            'efd': 'EFD',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 20,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination_paid: {
        pages: 1,
        page: 1,
        count: 1,
      },
      table_preloader_paid: false,
      table_review: {
        items: [],
        columns: ['id', 'date', 'legal_entity.name', 'responsible', 'business_unit.name', 'amount_total', 'paid', 'efd'],
        options: {
          headings: {
            'id': 'ID',
            'date': 'Date',
            'legal_entity.name': 'Legal entity',
            'responsible': 'Responsible',
            'business_unit.name': 'Business unit',
            'amount_total': 'Total',
            'paid': 'Paid',
            'efd': 'EFD',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 20,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination_review: {
        pages: 1,
        page: 1,
        count: 1,
      },
      table_preloader_review: false,
      business_unit_options: [],
      responsible_options: [],
      currency_options: [],
      responsible_approval_options: [],
      form: {},
    }
  },
  validations: {
    form: {
      legal_entity: { required },
      business_unit: { required },
      currency: { required },
      date: { required },
      responsible: { required },
      responsible_for_approval: { required },
    },
  },
  created() {
    Promise.all([
      getAdministrativeLegalEntities(),
      getCurrencyList({ local: true }),
      getEmployeesListSearch({ fund_approvals: true }),
      this.fetchItems(),
      this.fetchItemsRequested(),
      this.fetchItemsApproved(),
      this.fetchItemsPaid(),
      this.fetchItemsReview()
    ])
      .then(response => {
        this.legal_entity_options = response[0].data
        this.currency_options = response[1].data
        this.form.legal_entity = this.legal_entity_options[0].code
        this.onChangeLegalEntity()
        this.responsible_approval_options = response[2].data
        this.page_loaded = true
      })
  },
  methods: {
    async fetchItems() {
      this.table_preloader = true
      const response = await getFinanceFundRequests({
        page: this.pagination.page,
        page_size: 20,
        search: this.search,
        status: 'draft',
        legal_entity: this.search ? '' : this.filter_legal_entity,
      })
      this.table.items = response.data.results
      this.pagination.pages = response.data.pages
      this.pagination.count = response.data.count
      this.table_preloader = false
    },
    async fetchItemsRequested() {
      this.table_preloader_requested = true
      const response = await getFinanceFundRequests({
        page: this.pagination_requested.page,
        page_size: 20,
        search: this.search,
        status: 'requested',
        legal_entity: this.search ? '' : this.filter_legal_entity,
      })
      this.table_requested.items = response.data.results
      this.pagination_requested.pages = response.data.pages
      this.pagination_requested.count = response.data.count
      this.table_preloader_requested = false
    },
    async fetchItemsApproved() {
      this.table_preloader_approved = true
      const response = await getFinanceFundRequests({
        page: this.pagination_approved.page,
        page_size: 20,
        search: this.search,
        status: 'approved',
        paid: false,
        legal_entity: this.search ? '' : this.filter_legal_entity,
      })
      this.table_approved.items = response.data.results
      this.pagination_approved.pages = response.data.pages
      this.pagination_approved.count = response.data.count
      this.table_preloader_approved = false
    },
    async fetchItemsPaid() {
      this.table_preloader_paid = true
      const response = await getFinanceFundRequests({
        page: this.pagination_paid.page,
        page_size: 20,
        search: this.search,
        status: 'approved',
        paid: true,
        efd: false,
        legal_entity: this.search ? '' : this.filter_legal_entity,
      })
      this.table_paid.items = response.data.results
      this.pagination_paid.pages = response.data.pages
      this.pagination_paid.count = response.data.count
      this.table_preloader_paid = false
    },
    async fetchItemsReview() {
      this.table_preloader_review = true
      const response = await getFinanceFundRequests({
        page: this.pagination_review.page,
        page_size: 20,
        search: this.search,
        status: 'approved',
        paid: true,
        efd: true,
        legal_entity: this.search ? '' : this.filter_legal_entity,
      })
      this.table_review.items = response.data.results
      this.pagination_review.pages = response.data.pages
      this.pagination_review.count = response.data.count
      this.table_preloader_review = false
    },
    onClickPagination(page) {
      this.pagination.page = page
      this.fetchItems()
    },
    onClickPaginationRequested(page) {
      this.pagination_requested.page = page
      this.fetchItemsRequested()
    },
    onClickPaginationApproved(page) {
      this.pagination_approved.page = page
      this.fetchItemsApproved()
    },
    onClickPaginationPaid(page) {
      this.pagination_paid.page = page
      this.fetchItemsPaid()
    },
    onClickPaginationReview(page) {
      this.pagination_review.page = page
      this.fetchItemsReview()
    },
    onChangeFilter() {
      this.search = ''
      this.pagination.page = 1
      this.pagination_requested.page = 1
      this.pagination_approved.page = 1
      this.pagination_paid.page = 1
      this.fetchItems()
      this.fetchItemsRequested()
      this.fetchItemsApproved()
      this.fetchItemsPaid()
      this.fetchItemsReview()
    },
    async onChangeLegalEntity() {
      this.form.business_unit = null
      this.business_unit_options = []
      const response = await getAdministrativeBusinessUnits({ legal_entity: this.form.legal_entity })
      this.business_unit_options = response.data
    },
    onCheckForm() {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendForm()
      }
    },
    sendForm() {
      this.$refs.submit.preload = true
      const data = {...this.form, responsible: this.form.responsible.id}
      postFinanceFundRequests(data)
        .then(response => {
          this.$notify({
            type: 'success',
            title: 'Request created',
          })
          this.$router.push({ name: 'finance-fund-requests-detail-items', params: { id: response.data.id } })
        })
        .catch(() => { this.$refs.submit.preload = false })
    },
    onShowModal() {
      this.form = {
        business_unit: null,
        currency: this.currency_options[0].code,
        date: format(new Date(), 'yyyy-MM-dd'),
      }
      this.form.legal_entity = this.legal_entity_options[0].code
      this.onChangeLegalEntity()
      this.$v.form.$reset()
      this.$modal.show('request-form')
    },
    onCloseModal() {
      this.$modal.hide('request-form')
    },
    dateFormat(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy') : '-'
    },
    onItemSearch() {
      this.searchItem(this)
    },
    searchItem: debounce(vm => {
      vm.pagination.page = 1
      vm.pagination_requested.page = 1
      vm.pagination_approved.page = 1
      vm.pagination_paid.page = 1
      vm.pagination_review.page = 1
      vm.fetchItems()
      vm.fetchItemsRequested()
      vm.fetchItemsApproved()
      vm.fetchItemsPaid()
      vm.fetchItemsReview()
    }, 350),
    onEmployeeSearch(search, loading) {
      loading(true)
      this.searchEmployee(loading, search, this)
    },
    searchEmployee: debounce((loading, search, vm) => {
      if (!search) {
        loading(false)
        vm.responsible_options = []
      } else {
        getEmployeesListSearch({search}).then(response => {
          vm.responsible_options = response.data
          loading(false)
        })
      }
    }, 350),
  }
}
</script>
